import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import styles from "./blogPostPage.module.scss";

interface BlogReadControlProps {
    /** "loading" renders a disabled placeholder so the login prompt never flashes for signed-in readers. */
    authStatus: string;
    loading: boolean;
    isRead: boolean;
    onMarkRead: () => void;
    onMarkUnread: () => void;
    loginPath: string;
    atEnd?: boolean;
}

export default function BlogReadControl({
    authStatus,
    loading,
    isRead,
    onMarkRead,
    onMarkUnread,
    loginPath,
    atEnd = false,
}: BlogReadControlProps) {
    const { t } = useTranslation("blogPage");
    const label = atEnd ? t("post.readControl.labelEnd") : t("post.readControl.label");

    if (authStatus === "unauthenticated") {
        return (
            <div role="group" aria-label={label}>
                <p className={styles.readControlHint}>
                    <Link to={loginPath} className={styles.inlineLink}>
                        {t("post.readControl.loginPrompt")}
                    </Link>
                </p>
            </div>
        );
    }

    const pending = authStatus !== "authenticated";
    return (
        <div className={styles.readControl} role="group" aria-label={label}>
            {/* One toggle in a stable position keeps keyboard focus when its label changes. */}
            <button
                type="button"
                className={styles.readControlButton}
                onClick={isRead ? onMarkUnread : onMarkRead}
                disabled={pending || loading}
            >
                {isRead ? t("post.readControl.markUnread") : t("post.readControl.markRead")}
            </button>
            <span className={styles.readState} aria-live="polite">
                {isRead && (
                    <>
                        <FontAwesomeIcon icon={faCircleCheck} aria-hidden="true" />
                        {t("post.readControl.read")}
                    </>
                )}
            </span>
        </div>
    );
}
